import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import type { AttentionItem } from "../dashboard-selectors";

/**
 * Operational insight strip — design port (ALL-5825, dashboard lane task 6).
 *
 * The prototype narrates five inputs: a 30-day transfer count, the largest-AUM
 * chain, the pending-approval count, a degraded-provider count and an active
 * alert count. Four of those have no source in this app — `getTransferStats`,
 * provider health and alert counts are all `features/oversight/oversight-data`
 * fixtures, and the provider/alert tiles beside it (lane tasks 4–5) render
 * not-available states for the same reason. Narrating them here would put
 * invented operational facts on the dashboard, so this strip is restricted to
 * the two inputs the page actually holds:
 *
 *   - `topChain` — derived from the wallets the dashboard already fetched
 *     (`getTopChainByAum`), an AUM share and not a health claim;
 *   - `pendingApprovalsCount` — the same unsliced `useApprovals` list that
 *     feeds the Hero KPI and the attention bar.
 *
 * `hasError` is required so the strip never claims the dashboard is clear when
 * the sources behind it failed: an unknown state is not a clear one (the same
 * gate the attention bar carries).
 *
 * The empty-attention branch is deliberately neutral. `attentionItems` holds
 * only pending approvals and unread notifications, so "nothing needs your
 * attention" is the strongest true statement it can make — it is not a health
 * claim about providers, chains or alerts, none of which this strip reads.
 * (An earlier port rendered a success-toned "All operational indicators are
 * healthy." with a link to the deferred `/monitoring` page; both said more than
 * the data supports.)
 */
export function OperationalInsight({
  attentionItems,
  pendingCount,
  topChain,
  hasError,
}: {
  attentionItems: AttentionItem[];
  /** 0 when the approval source is unavailable or not permitted. */
  pendingCount: number;
  /** Largest chain by AUM, or null while wallets are loading, errored or empty. */
  topChain: string | null;
  /** True when any source behind this strip failed to load. */
  hasError: boolean;
}) {
  if (hasError) {
    return (
      <div
        role="status"
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning-border bg-warning-soft px-4 py-3 text-sm text-ink"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-warning" aria-hidden />
          <span className="font-semibold">Operational insight:</span>
          <span className="text-muted">
            Some dashboard sources did not load, so this summary is incomplete.
          </span>
        </div>
      </div>
    );
  }

  const insights: string[] = [];
  if (topChain) {
    insights.push(`${topChain} drives the largest AUM share`);
  }
  if (pendingCount > 0) {
    insights.push(`${pendingCount} approval${pendingCount === 1 ? "" : "s"} pending`);
  }

  // Nothing needs attention. Neutral, not success-toned: the two sources behind
  // this strip measure approvals and notifications, not operational health, and
  // there is no monitoring endpoint to link to (the `/monitoring` page is
  // deferred), so the strip states the absence instead of claiming a clean bill.
  if (attentionItems.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-4 py-3 text-sm">
        <Sparkles className="size-4 text-muted" aria-hidden />
        <span className="font-semibold text-ink">Operational insight:</span>
        <span className="text-muted">Nothing needs your attention.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-ink">
        <Sparkles className="size-4 text-primary" aria-hidden />
        <span className="font-semibold">Operational insight:</span>
        <span className="text-muted">
          {insights.length > 0 ? insights.join(" · ") : "Items below need attention."}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {attentionItems.slice(0, 4).map((item) => (
          <Link
            key={item.text}
            to={item.href}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              item.tone === "danger"
                ? "bg-danger-soft text-danger"
                : item.tone === "warning"
                  ? "bg-warning-soft text-warning"
                  : "bg-info-soft text-info"
            }`}
          >
            {item.text}
          </Link>
        ))}
      </div>
    </div>
  );
}
